import {
  bestContrast,
  CONTRAST_NON_TEXT,
  CONTRAST_TEXT,
  ensureContrast,
  mix,
  parseHex,
  relativeLuminance,
  shiftLightness,
  toHex,
  withAlpha,
} from './color.ts'
import {
  DARK_TONES,
  DEFAULT_BRAND_COLOR,
  LIGHT_TONES,
  neutral,
  type ToneName,
  type TonePalette,
  type TonePalettes,
} from './palette.ts'

/** Which of the two designed themes a set of colours belongs to. */
export type ColorScheme = 'light' | 'dark'

/** The brand tone: a feedback tone plus the interaction states a control needs. */
export type BrandColors = TonePalette & {
  /** The fill under a pointer or a held press, one step further from `onSolid`. */
  readonly hover: string
  readonly pressed: string
  /** The focus ring. Legible against every surface in the theme. */
  readonly ring: string
  /**
   * The soft halo drawn around a focused field, outside its `border` (the design's
   * `--brand-ring`: the brand at 55% opacity). Translucent by design — it is the second
   * channel; the 3:1 `border` is the first — so it is the one brand value that is not hex.
   */
  readonly halo: string
  /**
   * The decorative brand edge: a selected chip's outline, the active row's hairline (the
   * design's `--brand-border`). It carries no meaning on its own — the fill and the label
   * do — so unlike `border` it is not corrected to 3:1.
   */
  readonly soft: string
  /** The faintest brand wash: a table row under the pointer (the design's 4% tint). */
  readonly wash: string
  /** True when the supplied colour did not parse and the default brand was used. */
  readonly fallback: boolean
  /**
   * True when the supplied brand colour **itself had to move** to be used as the fill in this
   * scheme (`solid` differs from the supplied colour, compared as normalised hex so case and
   * `#rgb`/`#rrggbb` spelling never count). `text` and `border` are derived readability
   * colours that move for nearly every tenant colour — pure red is a fine fill and still has to
   * be lightened to be read on the dark canvas — so they do **not** make a colour "corrected":
   * a flag that watched them read "Corrected" for practically every tenant and so told the
   * platform admin nothing (`docs/DECISIONS.md`, "A tenant's brand colour is corrected, never
   * rejected"). The `text` and `border` values are still returned, and still corrected.
   */
  readonly corrected: boolean
}

export type TextColors = {
  readonly primary: string
  readonly secondary: string
  readonly muted: string
  readonly disabled: string
  /** Text on a dark block in light mode, and on a light block in dark mode. */
  readonly inverse: string
}

export type ThemeColors = {
  readonly scheme: ColorScheme
  /** The page behind everything. */
  readonly canvas: string
  /** A card, a row, a sheet — the surface content sits on. */
  readonly surface: string
  /** A surface that has been lifted: a menu, a modal, a toast. */
  readonly surfaceRaised: string
  /** A recessed area: an input's resting fill, a table header, a disabled block. */
  readonly surfaceSunken: string
  /** The worksheet rule. Decorative separation, not a control boundary. */
  readonly border: string
  /** A heavier rule, for the edge of a block rather than between its rows. */
  readonly borderStrong: string
  /** The boundary of an interactive control, held at 3:1 as WCAG 1.4.11 requires. */
  readonly borderInteractive: string
  /**
   * What a field the browser filled in (Chrome's "autofill") is painted: a lavender tint a step
   * stronger than `brand.surface`, which on a white page is invisible. It replaces the browser's own
   * pale blue on the web (`autofill-style.ts`), so a filled field is still told apart from a typed
   * one — by this fill and the brand border, neither of which is the text. The same two values in
   * every tenant, as the platform console draws them (`--autofill-surface`).
   */
  readonly autofillSurface: string
  /** The layer that dims the page behind a modal or a sheet. */
  readonly scrim: string
  /** The colour every elevation's shadow is drawn in. */
  readonly shadow: string
  readonly skeleton: { readonly base: string; readonly highlight: string }
  /**
   * Ink laid **over a photograph** — an uploading tile's dim layer and progress ring. The same
   * in both schemes, because what is behind it is a picture, not the theme's surface.
   */
  readonly media: { readonly dim: string; readonly track: string; readonly ink: string }
  readonly text: TextColors
  readonly brand: BrandColors
  readonly tone: Readonly<Record<ToneName, TonePalette>>
}

type SchemeSurfaces = {
  readonly canvas: string
  readonly surface: string
  readonly surfaceRaised: string
  readonly surfaceSunken: string
  readonly border: string
  readonly borderStrong: string
  readonly borderInteractive: string
  readonly autofillSurface: string
  readonly scrim: string
  readonly shadow: string
  readonly skeleton: { readonly base: string; readonly highlight: string }
  readonly text: TextColors
  readonly tones: TonePalettes
  /** The colour every brand fill puts its text in, fixed by the scheme. See `brandColors`. */
  readonly onBrand: string
}

const LIGHT: SchemeSurfaces = {
  canvas: neutral['050'],
  surface: neutral['000'],
  surfaceRaised: neutral['000'],
  surfaceSunken: neutral['100'],
  border: neutral['200'],
  borderStrong: neutral['300'],
  // The design draws the field border at #8a95a3, which is 3:1 on white only — 2.8:1 on the
  // canvas and 2.7:1 on a sunken block. WCAG 1.4.11 wants 3:1 on every surface a control
  // sits on, and `colors.test.ts` holds it there, so the light value stays the darker one.
  borderInteractive: '#7c8693',
  autofillSurface: '#e8e5fb',
  scrim: withAlpha('#0c1016', 0.48),
  shadow: '#101828',
  skeleton: { base: neutral['100'], highlight: neutral['050'] },
  text: {
    primary: neutral['950'],
    secondary: neutral['700'],
    muted: '#58626e',
    // The design's `--disabled` (#9aa3ae) is 2.3:1 on a sunken block. This system does not
    // take WCAG 1.4.3's exemption for inactive controls (see the note at the foot of
    // `scales.ts`), so a disabled label stays at 3:1 or better.
    disabled: '#767f8d',
    inverse: neutral['000'],
  },
  tones: LIGHT_TONES,
  onBrand: '#ffffff',
}

const DARK: SchemeSurfaces = {
  canvas: '#101419',
  surface: '#191f26',
  surfaceRaised: '#222932',
  surfaceSunken: '#0c1014',
  border: '#2a323c',
  borderStrong: '#3a4450',
  borderInteractive: '#6b7785',
  autofillSurface: '#27234d',
  scrim: withAlpha('#000000', 0.62),
  shadow: '#000000',
  skeleton: { base: '#232a33', highlight: '#2e3742' },
  text: {
    primary: '#eef1f4',
    secondary: '#c4ccd5',
    muted: '#9aa5b1',
    disabled: '#6b7482',
    inverse: neutral['1000'],
  },
  tones: DARK_TONES,
  onBrand: neutral['950'],
}

const MEDIA = {
  dim: withAlpha('#000000', 0.42),
  track: withAlpha('#ffffff', 0.35),
  ink: '#ffffff',
} as const

const SCHEMES: Readonly<Record<ColorScheme, SchemeSurfaces>> = { light: LIGHT, dark: DARK }

/**
 * The backgrounds a brand-coloured *text* has to stay legible on (and the ring, which is the text
 * colour). The brand wash is added by the caller once it exists, because it is derived from the
 * brand fill. The autofill surface is **not** here: brand text is never drawn on it.
 */
function backgroundsOf(surfaces: SchemeSurfaces): readonly string[] {
  return [surfaces.canvas, surfaces.surface, surfaces.surfaceRaised, surfaces.surfaceSunken]
}

/**
 * The backgrounds the brand *border* has to hold 3:1 on: those of {@link backgroundsOf}, plus the
 * autofill surface, because the brand border is drawn on a browser-filled field at rest
 * (`fix-apex-and-autofill`). Kept as its own list so that text and ring do not move: the tint is
 * the darkest light background, and correcting text against it would shift `brand.text` for most
 * light-scheme tenant colours for a surface text is never drawn on.
 */
function borderBackgroundsOf(surfaces: SchemeSurfaces): readonly string[] {
  return [...backgroundsOf(surfaces), surfaces.autofillSurface]
}

/**
 * The background in `candidates` that a foreground of this scheme finds hardest.
 *
 * In light mode foreground text is dark, so the *darkest* background is the worst case;
 * in dark mode it is the lightest. Correcting against the worst case corrects against all
 * of them, because contrast is monotonic in the background's luminance once the
 * foreground is on the far side of it — which is exactly what a scheme guarantees.
 */
export function hardestBackground(scheme: ColorScheme, candidates: readonly string[]): string {
  let hardest = candidates[0] ?? '#ffffff'
  for (const candidate of candidates) {
    const better =
      scheme === 'light'
        ? luminance(candidate) < luminance(hardest)
        : luminance(candidate) > luminance(hardest)
    if (better) {
      hardest = candidate
    }
  }
  return hardest
}

function luminance(value: string): number {
  const color = parseHex(value)
  return color === undefined ? 0 : relativeLuminance(color)
}

/** A colour as lowercase `#rrggbb`, so two spellings of one colour compare equal. */
function normalisedHex(value: string): string {
  const color = parseHex(value)
  return color === undefined ? value : toHex(color)
}

/**
 * Turn a tenant's brand colour into a usable tone.
 *
 * The rule, in one line: **the scheme fixes the colour text is written in on a brand
 * fill, and the fill is then corrected until it can carry that text at 4.5:1.**
 *
 * Fixing `onBrand` per scheme rather than choosing it per colour is what makes the rest
 * fall out for free. In light mode `onBrand` is white, so a fill that carries it at 4.5:1
 * has a relative luminance of at most 0.183 — dark enough that it is automatically
 * distinguishable from a near-white canvas by more than 3:1, as WCAG 1.4.11 requires of a
 * control. In dark mode `onBrand` is the ink neutral, the correction runs the other way,
 * and the same argument holds against a near-black canvas. One correction, three
 * guarantees, and no iteration that could fail to settle.
 *
 * A colour that already passes is returned untouched: a tenant that picked well sees
 * exactly the colour it picked. `corrected` says whether the *fill* had to move — whether
 * the colour the tenant supplied is not the colour its buttons are drawn in. The brand text
 * and border are corrected independently, and against different targets, and moving them is
 * ordinary; they do not set the flag. The platform admin can then warn at the point the colour
 * is chosen rather than leaving the tenant to wonder why its app is a slightly different
 * purple.
 */
export function brandColors(scheme: ColorScheme, supplied: string): BrandColors {
  const surfaces = SCHEMES[scheme]
  const fallback = parseHex(supplied) === undefined
  const source = fallback ? DEFAULT_BRAND_COLOR : supplied

  const onSolid = surfaces.onBrand
  const solid = ensureContrast(source, onSolid, CONTRAST_TEXT)
  const step = scheme === 'light' ? -0.05 : 0.05

  // The design's `--brand-surface` — the brand at 9% (22% in dark) over the surface.
  const surface = mix(surfaces.surface, solid, scheme === 'light' ? 0.09 : 0.22)
  const backgrounds = [...backgroundsOf(surfaces), surface]
  const hardest = hardestBackground(scheme, backgrounds)
  const hardestForBorder = hardestBackground(scheme, [...borderBackgroundsOf(surfaces), surface])

  const text = ensureContrast(source, hardest, CONTRAST_TEXT)
  const border = ensureContrast(source, hardestForBorder, CONTRAST_NON_TEXT)

  return {
    solid,
    onSolid,
    text,
    surface,
    border,
    hover: shiftLightness(solid, step),
    pressed: shiftLightness(solid, step * 2),
    ring: text,
    halo: withAlpha(solid, 0.55),
    soft: mix(surfaces.surface, solid, scheme === 'light' ? 0.45 : 0.6),
    wash: mix(surfaces.surface, solid, scheme === 'light' ? 0.04 : 0.1),
    fallback,
    // The fill only: "the colour you supplied is not the colour your buttons use". Compared as
    // normalised hex — `#4C3FBF`, `#4c3fbf` and a `#rgb` shorthand are one colour. `text` and
    // `border` are expected to move (pure red in dark mode is a fine fill and unreadable as
    // text), so counting them made nearly every tenant read "Corrected".
    corrected: normalisedHex(solid) !== normalisedHex(source),
  }
}

/**
 * The full colour set for one scheme and one brand colour.
 *
 * Nothing is memoised here: the whole build is a few dozen arithmetic operations, and a
 * cache keyed on a string that arrives from the network is a cache-invalidation problem
 * in exchange for microseconds. `ThemeProvider` memoises the one theme the tree is using.
 */
export function buildColors(scheme: ColorScheme, brand: string): ThemeColors {
  const surfaces = SCHEMES[scheme]

  return {
    scheme,
    canvas: surfaces.canvas,
    surface: surfaces.surface,
    surfaceRaised: surfaces.surfaceRaised,
    surfaceSunken: surfaces.surfaceSunken,
    border: surfaces.border,
    borderStrong: surfaces.borderStrong,
    borderInteractive: surfaces.borderInteractive,
    autofillSurface: surfaces.autofillSurface,
    scrim: surfaces.scrim,
    shadow: surfaces.shadow,
    skeleton: surfaces.skeleton,
    media: MEDIA,
    text: surfaces.text,
    brand: brandColors(scheme, brand),
    tone: surfaces.tones,
  }
}

/**
 * Whichever of the theme's own text colours is legible on an arbitrary block.
 *
 * The escape hatch for the one case tokens cannot cover: a colour that arrived from
 * outside the system — a tenant's brand wash behind a custom illustration, say. It picks
 * from the theme's palette rather than inventing a colour, so the result still belongs to
 * the theme.
 */
export function onSurface(colors: ThemeColors, background: string): string {
  return bestContrast(background, [colors.text.primary, colors.text.inverse])
}
